import { defineStore, acceptHMRUpdate } from "pinia";
import {
  TrainingClient,
  TrainingProgramDTO,
  TrainingModuleDTO,
  TestDTO,
  QuestionDTO,
  AnswerDTO,
  SimpleTrainingProgramDTO,
  ImportTrainingProgramsFromPortalRequest,
  BulkRemoveTrainingProgramPermissionRequest,
  BulkAddTrainingProgramPermissionRequest,
} from "./generatedFetchClient";

let trainingClient = new TrainingClient();

// A store instance to manage user and login state
export const useTrainingStore = defineStore({
  id: "trainingStore",
  state: () => {
    return {
      trainingProgramList: [] as SimpleTrainingProgramDTO[],
      portalTrainingPrograms: [] as TrainingProgramDTO[],
      selectedTrainingProgram: null as TrainingProgramDTO | null,
      selectedTrainingModule: null as TrainingModuleDTO | null,
      selectedModuleTests: null as TestDTO[] | null,
      selectedTest: null as TestDTO | null,
      selectedTestQuestions: [] as QuestionDTO[] | null,
      selectedQuestion: null as QuestionDTO | null,

      clientAnswers: [] as number[],
    };
  },
  actions: {
    setAuthToken(token: string) {
      trainingClient.setAuthToken(token);
    },
    async unsubscribeTraining(pid: number, token: string): Promise<string> {
      try {
        await trainingClient.unsubscribeTraining(pid, token);
        return "";
      } catch (apiError: any) {
        console.error(apiError);
        return apiError;
      }
    },
    async getTrainingProgramList(force: boolean = false): Promise<string> {
      try {
        if (this.trainingProgramList.length === 0 || force)
          this.trainingProgramList = await trainingClient.getTrainingProgramList();
        return "";
      } catch (apiError: any) {
        console.error(`Error getting training program list` + apiError);
        return apiError.response;
      }
    },
    async getTrainingProgram(trainingProgramId: number): Promise<string> {
      try {
        console.log("Get Training Program for trainingProgamId: " + trainingProgramId);

        // TODO: Check if trainingPrograms list is already loaded to pull data from
        let trainingProgram = await trainingClient.getTrainingProgram(trainingProgramId);
        if (trainingProgram) {
          this.selectedTrainingProgram = trainingProgram;
        }
        return "";
      } catch (error) {
        let msg = `error getting training program with id: ${trainingProgramId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getTrainingProgramsByPortal(portalId: number): Promise<string> {
      try {
        let trainingPrograms = await trainingClient.getTrainingProgramsByPortal(portalId);
        if (trainingPrograms) {
          this.portalTrainingPrograms = trainingPrograms;
          //console.log("Training Programs loaded", trainingPrograms);
        }
        return "";
      } catch (error) {
        let msg = `error getting training programs with portal id: ${portalId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getTrainingModule(trainingModuleId: number): Promise<string> {
      try {
        // TODO: Check if trainingPrograms list/selectedTrainingProgram is already loaded to pull data from
        let trainingModule = await trainingClient.getTrainingModule(trainingModuleId);
        if (trainingModule) {
          this.selectedTrainingModule = trainingModule;
        }
        return "";
      } catch (error) {
        let msg = `error getting training module with id: ${trainingModuleId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getTest(testId: number): Promise<string> {
      try {
        let test = await trainingClient.getTest(testId);
        if (test) {
          this.selectedTest = test;
        }
        return "";
      } catch (error) {
        let msg = `error getting test with id: ${testId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getTestsByTrainingModule(trainingModuleId: number): Promise<string> {
      try {
        let selectedModuleTests = await trainingClient.getTestsByTrainingModule(trainingModuleId);
        if (selectedModuleTests) {
          this.selectedModuleTests = selectedModuleTests;
        }
        return "";
      } catch (error) {
        let msg = `error getting tests with module id: ${trainingModuleId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getQuestion(questionId: number): Promise<string> {
      try {
        let question = await trainingClient.getQuestion(questionId);
        if (question) {
          this.selectedQuestion = question;
        }
        return "";
      } catch (error) {
        let msg = `error getting question with id: ${questionId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async getQuestionsByTest(testId: number): Promise<string> {
      try {
        let selectedTestQuestions = await trainingClient.getQuestionsByTest(testId);
        if (selectedTestQuestions) {
          this.selectedTestQuestions = selectedTestQuestions;
        }
        return "";
      } catch (error) {
        let msg = `error getting questions with test id: ${testId}` + error;
        console.error(msg);
        return msg;
      }
    },
    async saveTrainingProgram(saveProgram: TrainingProgramDTO): Promise<string> {
      try {
        await trainingClient.saveTrainingProgram(saveProgram);
        return "";
      } catch (apiError: any) {
        console.error("Error saving training program", apiError);
        return apiError.response;
      }
    },
    async importTrainingProgramsFromPortal(
      request: ImportTrainingProgramsFromPortalRequest
    ): Promise<string> {
      try {
        await trainingClient.importTrainingProgramsFromPortal(request);
        return "";
      } catch (apiError: any) {
        console.error("Error importing training program(s) from portal", apiError);
        return apiError.response;
      }
    },
    async saveTrainingModule(saveModule: TrainingModuleDTO): Promise<string> {
      try {
        await trainingClient.saveTrainingModule(saveModule);
        return "";
      } catch (apiError: any) {
        console.error("Error saving training module", apiError);
        return apiError.response;
      }
    },
    async saveTest(saveTest: TestDTO): Promise<string> {
      try {
        await trainingClient.saveTest(saveTest);
        return "";
      } catch (apiError: any) {
        console.error("Error saving test", apiError);
        return apiError.response;
      }
    },
    async saveQuestion(saveQuestion: QuestionDTO): Promise<string> {
      try {
        await trainingClient.saveQuestion(saveQuestion);
        return "";
      } catch (apiError: any) {
        console.error("Error saving question", apiError);
        return apiError.response;
      }
    },
    async saveAnswer(saveAnswer: AnswerDTO): Promise<string> {
      try {
        await trainingClient.saveAnswer(saveAnswer);
        return "";
      } catch (apiError: any) {
        console.error("Error saving answer", apiError);
        return apiError.response;
      }
    },
    async saveResult(saveResult: any): Promise<string> {
      try {
        await trainingClient.saveResult(saveResult);
        return "";
      } catch (apiError: any) {
        console.error("Error saving result", apiError);
        return apiError.response;
      }
    },
    async deleteTrainingProgram(trainingProgramId: number): Promise<string> {
      try {
        await trainingClient.deleteTrainingProgram(trainingProgramId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting training program", apiError);
        return apiError.response;
      }
    },
    async deleteTrainingModule(trainingModuleId: number): Promise<string> {
      try {
        await trainingClient.deleteTrainingModule(trainingModuleId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting training module", apiError);
        return apiError.response;
      }
    },
    async deleteTest(testId: number): Promise<string> {
      try {
        await trainingClient.deleteTest(testId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting test", apiError);
        return apiError.response;
      }
    },
    async deleteQuestion(questionId: number): Promise<string> {
      try {
        await trainingClient.deleteQuestion(questionId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting question", apiError);
        return apiError.response;
      }
    },
    async deleteAnswer(answerId: number): Promise<string> {
      try {
        await trainingClient.deleteAnswer(answerId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting answer", apiError);
        return apiError.response;
      }
    },
    async bulkRemoveTrainingProgramPermission(
      portalId: number,
      trainingProgramId: number
    ): Promise<{ error: string; count: number }> {
      try {
        const request: BulkRemoveTrainingProgramPermissionRequest = { portalId, trainingProgramId };
        const count = await trainingClient.bulkRemoveTrainingProgramPermission(request);
        return { error: "", count: count ?? 0 };
      } catch (apiError: any) {
        console.error("Error bulk removing training program permissions", apiError);
        return { error: apiError.response ?? "An unexpected error occurred.", count: 0 };
      }
    },
    async bulkAddTrainingProgramPermission(
      portalId: number,
      trainingProgramId: number
    ): Promise<{ error: string; count: number }> {
      try {
        const request: BulkAddTrainingProgramPermissionRequest = { portalId, trainingProgramId };
        const count = await trainingClient.bulkAddTrainingProgramPermission(request);
        return { error: "", count: count ?? 0 };
      } catch (apiError: any) {
        console.error("Error bulk adding training program permissions", apiError);
        return { error: apiError.response ?? "An unexpected error occurred.", count: 0 };
      }
    },
    getTrainingProgamId(moduleId: number): number | null {
      if (this.portalTrainingPrograms?.length > 0) {
        var programForModule = this.portalTrainingPrograms.find((tp) =>
          tp?.trainingModules?.map((tm) => tm.trainingModuleId).includes(moduleId)
        );
        if (programForModule?.trainingProgramId || 0 > 0) {
          return programForModule?.trainingProgramId || null;
        } else {
          console.error("No training program loaded for module " + moduleId);
        }
      } else {
        console.error("Cannot lookup TrainingProgramId until training Programs are loaded");
      }
      return null;
    },
  },
  getters: {},
});

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(
    acceptHMRUpdate(useTrainingStore as any, (import.meta as any).hot)
  );
}
